import { useState, useEffect } from 'react';
import apiService from '../services/api';
import AddressSearch from './AddressSearch';
import { Button, ActionIcon, Checkbox } from '@mantine/core';
import { IconPlus, IconEdit } from '@tabler/icons-react';
import './AdminPickupLocations.css';

export default function AdminPickupLocations() {
  const [locations, setLocations] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', address: '', instructions: '', is_active: true, times: [] });

  useEffect(() => { loadLocations(); }, []);

  const loadLocations = async () => {
    try {
      const data = await apiService.getAdminPickupLocations();
      setLocations(data);
    } catch (err) { console.error(err); }
  };

  const saveLocation = async (e) => {
    e.preventDefault();
    if (editingId) {
      await apiService.updatePickupLocation(editingId, formData);
    } else {
      await apiService.createPickupLocation(formData);
    }
    setEditingId(null);
    setFormData({ name: '', address: '', instructions: '', is_active: true, times: [] });
    loadLocations();
  };

  const addTimeSlot = () => {
    setFormData({ ...formData, times: [...formData.times, { day_of_week: 'Monday', start_time: '09:00', end_time: '17:00' }] });
  };

  const removeTimeSlot = (index) => {
    const newT = formData.times.filter((_, idx) => idx !== index);
    setFormData({ ...formData, times: newT });
  };

  // Convert the structured search result into a single string for this form
  const handleAddressSelect = (addressData) => {
    const parts = [
      addressData.address_line1,
      `${addressData.postal_code} ${addressData.city}`.trim(),
      addressData.country
    ].filter(Boolean);
    
    setFormData(prev => ({ ...prev, address: parts.join(', ') }));
  };

  return (
    <div className="pickup-mgr-wrapper dashboard-view-wrapper">
      <header className="dashboard-page-header" style={{ marginBottom: '2rem' }}>
        <div>
          <h1 className="dashboard-page-title">Pickup Locations</h1>
          <p className="dashboard-page-description">Configure the physical pickup points available during customer checkout.</p>
        </div>
      </header>

      <div className="pickup-mgr-content">
        <form onSubmit={saveLocation} className="pickup-mgr-form">
          <h3>{editingId ? 'Edit Location' : 'Add New Location'}</h3>
          <div className="form-group mb-3">
            <label htmlFor="pickup-location-name">Location Name</label>
            <input
              id="pickup-location-name"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              required
              autoComplete="organization"
            />
          </div>

          <div className="form-group mb-3">
            <span className="label-like">Search Address</span>
            <AddressSearch
              id="pickup-location-address-search"
              onSelectResult={handleAddressSelect}
              showDeliveryDisclaimer={false}
            />
          </div>

          <div className="form-group mb-3">
            <label htmlFor="pickup-location-address">Full Address</label>
            <input
              id="pickup-location-address"
              value={formData.address}
              onChange={e => setFormData({ ...formData, address: e.target.value })}
              required
              autoComplete="street-address"
            />
          </div>
          <div className="form-group mb-3">
            <label htmlFor="pickup-location-instructions">Instructions (e.g. Ring the bell)</label>
            <textarea
              id="pickup-location-instructions"
              value={formData.instructions}
              onChange={e => setFormData({ ...formData, instructions: e.target.value })}
            />
          </div>
          <Checkbox
            label="Is Active"
            checked={formData.is_active}
            onChange={e => setFormData({ ...formData, is_active: e.currentTarget.checked })}
            mb="sm"
          />

          <hr />
          <h4 id="pickup-times-heading">Availability Windows</h4>
          {formData.times.map((t, i) => (
            <div key={i} className="time-slot-row" role="group" aria-labelledby="pickup-times-heading">
              <label htmlFor={`pickup-day-${i}`} className="sr-only">Day {i + 1}</label>
              <select
                id={`pickup-day-${i}`}
                aria-label={`Day for window ${i + 1}`}
                value={t.day_of_week}
                onChange={e => { const newT = [...formData.times]; newT[i].day_of_week = e.target.value; setFormData({ ...formData, times: newT }); }}
              >
                {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(d => <option key={d}>{d}</option>)}
              </select>
              <label htmlFor={`pickup-start-${i}`} className="sr-only">Start time</label>
              <input
                id={`pickup-start-${i}`}
                type="time"
                aria-label={`Start time for window ${i + 1}`}
                required
                value={t.start_time}
                onChange={e => { const newT = [...formData.times]; newT[i].start_time = e.target.value; setFormData({ ...formData, times: newT }); }}
              />
              <span aria-hidden="true">to</span>
              <label htmlFor={`pickup-end-${i}`} className="sr-only">End time</label>
              <input
                id={`pickup-end-${i}`}
                type="time"
                aria-label={`End time for window ${i + 1}`}
                required
                value={t.end_time}
                onChange={e => { const newT = [...formData.times]; newT[i].end_time = e.target.value; setFormData({ ...formData, times: newT }); }}
              />
              <ActionIcon
                color="red"
                variant="light"
                size="sm"
                onClick={() => removeTimeSlot(i)}
                aria-label={`Remove time window ${i + 1}`}
              >
                <span aria-hidden="true">✕</span>
              </ActionIcon>
            </div>
          ))}
          <Button
            variant="default"
            leftSection={<IconPlus size={14} aria-hidden="true" />}
            onClick={addTimeSlot}
          >
            Add Time Window
          </Button>

          <div className="form-actions">
            {editingId ? <Button variant="subtle" color="gray" onClick={() => { setEditingId(null); setFormData({ name: '', address: '', instructions: '', is_active: true, times: [] }); }}>Cancel</Button> : null}
            <Button type="submit" ml="sm">{editingId ? 'Update' : 'Create'} Location</Button>
          </div>
        </form>

        <div className="locations-list">
          {locations.map(loc => (
            <div key={loc.id} className={`location-card ${!loc.is_active ? 'inactive' : ''}`}>
              <div className="loc-info">
                <strong>{loc.name} {loc.is_active ? '' : '(Inactive)'}</strong>
                <p>{loc.address}</p>
                <small>{loc.times.length} time window(s)</small>
              </div>
              <Button
                variant="subtle"
                size="sm"
                leftSection={<IconEdit size={14} aria-hidden="true" />}
                onClick={() => { setEditingId(loc.id); setFormData(loc); }}
                aria-label={`Edit pickup location ${loc.name}`}
              >
                Edit
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}